import { Component, OnInit, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import { ApiDataService } from "src/app/shared/services/api.service";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { takeUntil } from "rxjs/operators";
import { AppRouteConstants } from "src/app/shared/constants/app-route.constants";
import { SharedDialogService } from "src/app/shared/services/shared-dialog.service";
import { StorageService } from "src/app/shared/services/storage.service";
import { Lesson } from "../../../lessons/models/lesson";
import { LessonsApiService } from "../../../lessons/services/lessons-api.service";
import { StudentSession } from "src/app/shared/models/student-session";
import { enagagementTabs, errorMessages } from "../../constants/passage-constants";

@Component({
    selector: "post-engagement",
    templateUrl: "./post-engagement.component.html",
    styleUrls: ["./post-engagement.component.css"],
    standalone: false
})

export class PostEngagement implements OnInit, OnDestroy {
  subscription: Subscription;
  loading: boolean = true;
  isSavingLoader: boolean = false
  lessons: Lesson[] = [];
  updateStudentProgressGroup: UntypedFormGroup;
  student: any;
  sessionId: any;
  wasStudentAbsent = false;
  isLoading = false;
  tutorTipModal=false;
  tutorTips="";
  passageDetails=[];
  firstStepNumber: number = 3
  sessionData: StudentSession;
  lessonName: string = '';

  private ngUnsubscribe = new Subject <void>();
  constructor(
    private apiService: ApiDataService,
    private lessonsApiService: LessonsApiService,
    private toastrService: ToastrService,
    public _location: Location,
    private router: Router,
    private sharedDialogService: SharedDialogService,
    private cdr: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef
  ) {
    this.initializeForm();
  }

  onBackClick() {
    const passageTabKey = enagagementTabs.passageEngagement
    this.goToTab(passageTabKey)
    StorageService.setSessionCurrentPage(passageTabKey);
    this.updateStudentProgressGroup.reset();
  }
  
  ngOnInit() {
    this.sessionData = StorageService.getSessionData();
    this.getLessonDetails()
    this.getTutorTips()
  }

  tutorTipModalOpen(value:boolean){
    this.tutorTipModal=value
  }

  @Output() changeTab = new EventEmitter<number>();
  
  goToTab(index: number) {
    this.changeTab.emit(index);
  }
  
  getTutorTips() { 
    try {
      this.apiService
      .getLessonTutorTips(this.sessionData["lesson"])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          this.tutorTips = res.tutor_tips;
        }
      });
    } catch (error) {
      console.error("Error fetching tutor tip:", error);
    }
  }

  get passages(): UntypedFormArray {
    return this.updateStudentProgressGroup.get('passages') as UntypedFormArray;
  }
  getLessonDetails() {
    this.lessonsApiService
      .getLesson(this.sessionData["lesson"])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response) => {
          if (response) {

            this.lessonName = response?.lesson_plan_name || '';
            this.passageDetails = response?.passage || [];
            const passagesFormArray = this.updateStudentProgressGroup.get('passages') as UntypedFormArray;

            this.passageDetails.forEach((passage) => {
              passagesFormArray.push(this.createPassageFormGroup(passage));
            });

            this.cd.detectChanges();
            this.getSessionDetails()
          } 
        },
        (err) => {
          this.loading = false;
        }
      );
  }

  getSessionDetails() { 
    try {
      const lessonStudentIds = { 
        lessonId: this.sessionData["lesson"], 
        studentId: this.sessionData["student"],
      };
      this.apiService
      .getSessionDetails(lessonStudentIds)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          if (res?.data?.length) {
            
            const sessionAttemptProgress = res?.data[0];
            this.updateStudentProgressGroup.controls["mood_second"].setValue(sessionAttemptProgress?.student_mood_second || "");
            this.updateStudentProgressGroup.controls["volunteer_mood"].setValue(sessionAttemptProgress?.volunteer_mood || "");
            this.updateStudentProgressGroup.controls["read_at_home"].setValue(sessionAttemptProgress?.read_at_home || "");
            this.updateStudentProgressGroup.controls["comments"].setValue(sessionAttemptProgress?.comments || "");
            
            res?.data?.forEach((secondReadProgress, index) => {
              this.setSecondReadComplete(secondReadProgress?.second_read_complete, index);
            })
          }
          this.loading = false;
        }
      });
    } catch (error) {
      this.loading = false;
      console.error("Error fetching tutor tip:", error);
    }
  }

  updatePostEngageProgress() {
    const formValue = this.updateStudentProgressGroup.value;
    const passagesFormArray = this.updateStudentProgressGroup.get('passages') as UntypedFormArray;

    // Current page means the next page to be loaded next time if the session is not fully completed!
    const progressData = {
      ...formValue,
      current_page: enagagementTabs.finalEngagement,
      lesson: this.sessionData["lesson"],
      volunteer: this.sessionData["volunteer"],
      student: this.sessionData["student"],
      curriculum_name: this.sessionData["curriculum_name"] || null,
      passages: passagesFormArray.value
    };
    
    this.updateProgress(progressData);
  }

  reviewEngagementForm () {
    this.updatePostEngageProgress();
  }

  setMoodValue(moodValue: number, controlName: string) {
    this.updateStudentProgressGroup.get(controlName).setValue(moodValue);
  }

  setSecondReadComplete(readValue: number, index: number) {
    try {
    const passagesFormArray = this.updateStudentProgressGroup.get('passages') as UntypedFormArray;
    if (passagesFormArray) {
      const passageGroup = passagesFormArray.at(index) as UntypedFormGroup;
      if (passageGroup?.get('second_read_complete')) {
        const second_read = passageGroup.get('second_read_complete') as UntypedFormControl;
        second_read.setValue(readValue);
        }
      } 
    }
    catch (error) {
      console.error("Error setting second read complete:", error);
    } 
  }

  createPassageFormGroup(passage): UntypedFormGroup {
    return this.fb.group({
      second_read_complete: new UntypedFormControl(passage?.second_read_complete || null),
      passage_id: new UntypedFormControl(passage?.id || ''),
      passage_name: new UntypedFormControl(passage?.passage_name || '')
    });
  }

  checkBoxChanges(event: any) {
    this.wasStudentAbsent = event.value;
    this.resetSelectedEngagement();
  }

  resetWordsMissed() {
    this.updateStudentProgressGroup.controls["words_missed_first"].setValue(0);
    this.updateStudentProgressGroup.controls["words_missed_second"].setValue(0);
    this.updateStudentProgressGroup.controls["total_words_learned"].setValue(0);
  }
  resetSelectedEngagement() {
    this.updateStudentProgressGroup.controls["mood_first"].setValue("");
    this.updateStudentProgressGroup.controls["mood_second"].setValue("");
    this.updateStudentProgressGroup.controls["volunteer_mood"].setValue("");
  }

  initializeForm() {
    this.updateStudentProgressGroup = new UntypedFormGroup({
      words_missed_first: new UntypedFormControl({ value: "0", disabled: true }),
      mood_first: new UntypedFormControl("", Validators.required),
      words_missed_second: new UntypedFormControl({ value: "0", disabled: true }),
      mood_second: new UntypedFormControl("", Validators.required),
      total_words_learned: new UntypedFormControl({ value: "0", disabled: true }),
      volunteer_mood: new UntypedFormControl("", Validators.required),
      comments: new UntypedFormControl(""),
      control_group: new UntypedFormControl(false),
      read_at_home: new UntypedFormControl("", Validators.required),
      passages: this.fb.array([]),
    });
  }

  createDefaultPassageGroups() {
    const defaultPassageGroups = [];
    for (let i = 0; i < 2; i++) {
      defaultPassageGroups.push(this.createDisabledPassageFormGroup());
    }
    return defaultPassageGroups;
  }

  createDisabledPassageFormGroup() {
    return this.fb.group({
      words_missed_first: new UntypedFormControl({ value: "", disabled: true }, [
        Validators.required,
      ]),
      words_missed_second: new UntypedFormControl({ value: "", disabled: true }, [
        Validators.required,
      ]),
      total_words_learned: new UntypedFormControl({ value: "", disabled: true }, [
        Validators.required,
      ]),
      second_read_complete: new UntypedFormControl({ value: 0, disabled: true }, [
        Validators.required,
      ]),
      passage_id: new UntypedFormControl({ value: '', disabled: true }),
      passage_name: new UntypedFormControl({ value: '', disabled: true }),
      word_count: new UntypedFormControl({ value: '', disabled: true }),
    });
  }

  createPassageGroups(passages) {
    return passages.map((passage) => {
      return this.fb.group({
        words_missed_first: new UntypedFormControl(passage.words_missed_first, [Validators.required]),
        words_missed_second: new UntypedFormControl(passage.words_missed_second, [Validators.required]),
        total_words_learned: new UntypedFormControl(passage.words_missed_first - passage.words_missed_second, [Validators.required]),
        second_read_complete: new UntypedFormControl(passage.second_read_complete || 0, [Validators.required]),
        passage_id: new UntypedFormControl(passage.passage_id),
        passage_name: new UntypedFormControl(passage.passage_name),
        word_count: new UntypedFormControl(passage.word_count),
      });
    });
  }

  onTerribleClick() {
    this.sharedDialogService.TutorEngagementAction();
  }

  checkAllFieldsFilled(progress: any) {
    if (
      !progress.read_at_home ||
      !progress.mood_second ||
      !progress.volunteer_mood ||
      !progress.passages?.length ||
      progress.passages?.find(passageEls => !passageEls?.second_read_complete)
    ) {
      this.toastrService.error("Please fill all the required fields before submitting!");
      return false;
    }
    return true;
  }

  updateProgress(progress) {
    if (this.checkAllFieldsFilled(progress)) {
      this.isSavingLoader = true
      this.apiService.updateSessionEngagement(progress).subscribe(
        (res) => {
          if (res) {
            const nextTabKey = progress?.current_page
            this.goToTab(nextTabKey)
            StorageService.setSessionCurrentPage(nextTabKey);
          }
          this.isSavingLoader = false
        },
        (err) => {
          this.toastrService.error(errorMessages.UPDATE_PROGRESS_FAILED);
          this.isSavingLoader = false
        }
      );
   }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription?.unsubscribe();
    }
  }
}
